import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Upload, Trash2, Image as ImageIcon, Maximize2, Copy } from "lucide-react";
import ImageLightbox from "@/components/ImageLightbox";
import AplicarImagenModal from "@/components/AplicarImagenModal";

/** Componente para subir la foto principal de un producto.
 *  - `productoId`: id del producto (requerido).
 *  - `productoNombre`: nombre a mostrar en el modal "aplicar a otros".
 *  - `tieneImagen`: bool o string; si tiene, muestra preview.
 *  - `onUpdated`: callback tras subir/borrar.
 */
export default function ProductoImagenUploader({ productoId, productoNombre, tieneImagen, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [ver, setVer] = useState(Date.now()); // para forzar recarga del preview
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [aplicarOpen, setAplicarOpen] = useState(false);
  // Estado local que se actualiza inmediatamente al subir/borrar — evita esperar
  // al refetch del padre para mostrar los botones "Aplicar a otros" y "Quitar".
  const [hasImageLocal, setHasImageLocal] = useState(!!tieneImagen);
  // Resincronizar si el padre cambia el producto (ej. abrir modal de edición de otro producto).
  useEffect(() => { setHasImageLocal(!!tieneImagen); }, [tieneImagen, productoId]);
  const hasImage = hasImageLocal;
  const inputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // 300px alcanza para el preview 96px @ 2x DPR. Lightbox usa 1200 para calidad al hacer zoom.
  const previewUrl = `${backendUrl}/api/catalogo/img/${productoId}?w=300&v=${ver}`;
  const lightboxUrl = `${backendUrl}/api/catalogo/img/${productoId}?w=1200&v=${ver}`;

  const subir = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("La imagen supera 5MB");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      return toast.error("Formato inválido: usa JPG, PNG o WebP");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/productos/${productoId}/imagen`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto actualizada — usa 'Aplicar a otros' para copiarla a las variantes", { duration: 5000 });
      setVer(Date.now());
      setHasImageLocal(true);  // Mostrar botones inmediatamente sin esperar al refetch del padre
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir");
    } finally { setUploading(false); }
  };

  const borrar = async () => {
    if (!window.confirm("¿Borrar la foto del producto?")) return;
    try {
      await api.delete(`/productos/${productoId}/imagen`);
      toast.success("Foto eliminada");
      setVer(Date.now());
      setHasImageLocal(false);
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="flex items-center gap-3">
      {hasImage ? (
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="relative group w-24 h-24 rounded-lg border border-border overflow-hidden bg-muted flex-shrink-0"
          data-testid="producto-imagen-preview-btn"
          title="Ver imagen en grande"
        >
          <img src={previewUrl} alt="Producto" loading="lazy" decoding="async"
               onError={(e) => (e.target.style.display = "none")}
               className="w-full h-full object-cover" data-testid="producto-imagen-preview" />
          <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Maximize2 className="w-6 h-6 text-white" />
          </div>
        </button>
      ) : (
        <div className="w-24 h-24 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground bg-muted/30" data-testid="producto-imagen-empty">
          <ImageIcon className="w-8 h-8" />
        </div>
      )}
      <div className="flex flex-col gap-2">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
               onChange={(e) => subir(e.target.files?.[0])}
               className="hidden" data-testid="producto-imagen-input" />
        <Button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                variant="outline" size="sm" className="h-9" data-testid="producto-imagen-upload-btn">
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? "Subiendo..." : (hasImage ? "Cambiar foto" : "Subir foto")}
        </Button>
        {hasImage && (
          <Button type="button" onClick={() => setAplicarOpen(true)} variant="ghost" size="sm"
                  className="h-8 text-blue-700 hover:bg-blue-50 border border-blue-200" data-testid="producto-imagen-aplicar-btn">
            <Copy className="w-3.5 h-3.5 mr-1" /> Aplicar a otros
          </Button>
        )}
        {hasImage && (
          <Button type="button" onClick={borrar} variant="ghost" size="sm"
                  className="h-8 text-rose-600 hover:bg-rose-50" data-testid="producto-imagen-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar
          </Button>
        )}
        <div className="text-[10px] text-muted-foreground">JPG, PNG o WebP · Máx 5MB · Click en la foto para verla grande</div>
      </div>
      <ImageLightbox src={hasImage ? lightboxUrl : null} alt="" open={lightboxOpen} onOpenChange={setLightboxOpen} />
      <AplicarImagenModal
        open={aplicarOpen}
        onOpenChange={setAplicarOpen}
        sourceId={productoId}
        sourceNombre={productoNombre}
        onApplied={() => { onUpdated && onUpdated(); }}
      />
    </div>
  );
}
